import { ResultOf } from '@graphql-typed-document-node/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTicketsClient } from 'services/api';
import { CartQuery, CartUpdateDocument } from 'types/api/tickets.types';

import { ticketsCartQuery } from './queries';
import { AppSyncTicketsGraphQLError } from './types';
import { useCartVersion } from './useCartVersion';
import { isNotFound, useTicketingCartId, useTicketingEventId } from './utils';

type RemoveCartItemVariables = {
  itemIds: Array<string>;
};

export const useTicketsRemoveCartItem = () => {
  const { cartId, setCartId } = useTicketingCartId();
  const { eventId } = useTicketingEventId();
  const version = useCartVersion();
  const queryClient = useQueryClient();
  const ticketsClient = useTicketsClient();

  const { isPending, isPaused, mutate, error } = useMutation<
    ResultOf<typeof CartUpdateDocument>,
    AppSyncTicketsGraphQLError,
    RemoveCartItemVariables,
    { previousValue?: CartQuery }
  >({
    mutationFn: ({ itemIds }) =>
      ticketsClient.request(CartUpdateDocument, {
        cartId,
        eventId,
        input: {
          action: {
            action: 'RemoveItems',
            itemIds,
          },
          version,
        },
      }),
    onMutate: async ({ itemIds }: RemoveCartItemVariables) => {
      const queryKey = ticketsCartQuery({ eventId, cartId }).queryKey;
      await queryClient.cancelQueries({ queryKey });
      const cart = queryClient.getQueryData<CartQuery>(queryKey);
      if (!!cart?.cart) {
        const updatedCart = {
          ...(cart?.cart ?? {}),
          items:
            cart.cart.items?.filter((item) => !itemIds.includes(item.id)) ?? [],
        };

        queryClient.setQueryData(queryKey, {
          cart: updatedCart,
        });
      }
      return { previousValue: cart };
    },
    onError: (error, _v, context) => {
      const e = error;
      if (isNotFound(e)) setCartId('');
      else if (context?.previousValue) {
        queryClient.setQueryData(
          ticketsCartQuery({ eventId, cartId }).queryKey,
          context.previousValue,
        );
      }
    },
    onSuccess: ({ cart }) => {
      if (cart?.id) {
        queryClient.setQueryData(
          ticketsCartQuery({ eventId, cartId: cart.id }).queryKey,
          { cart },
        );
        setCartId(cart.id);
      } else {
        queryClient.removeQueries({
          queryKey: ticketsCartQuery({ eventId, cartId }).queryKey,
        });
        setCartId('');
      }
    },
  });

  return {
    isLoading: isPending && !isPaused,
    removeCartItem: mutate,
    error,
  };
};
